// @ts-strict-ignore
import { allFileTypeExtensions } from "../constants";
import type {
  SupportedMediaLibraryExtensions,
  SupportedTypes,
  AllowedFilters,
  FileExtensionOption,
  FileTypeLabels,
  GroupedFileTypeOptions,
} from "../types";
import { uppercaseFirstLetter } from "./uppercaseFirstLetter";

/**
 * Given a list of filters,
 */
export const allowedFiltersToOptions = ({
  allowedFilters,
  selectAllFileLabels,
  allFileTypes,
}: {
  allowedFilters: AllowedFilters;
  selectAllFileLabels: FileTypeLabels;
  allFileTypes: string;
}): FileExtensionOption[] | GroupedFileTypeOptions[] => {
  const sections = Object.keys(allowedFilters) as SupportedTypes[];

  const allFileOption: GroupedFileTypeOptions = {
    options: [
      {
        label: allFileTypes,
        value: "",
      },
    ],
    type: "other",
  };

  const showAllOption =
    sections.length === 1 &&
    Object.keys(allowedFilters[sections[0]]).length === Object.values(allFileTypeExtensions[sections[0]]).length;

  if (sections.length === 1 && showAllOption) {
    return sections.map((type): GroupedFileTypeOptions => {
      const allowedFileTypes = Object.keys(allowedFilters[type]) as SupportedMediaLibraryExtensions[];
      // if we allow all file types within a grouping, display the select all option, otherwise don't
      const options = allowedFileTypes.map((ext) => allFileTypeExtensions[type][ext]);
      return {
        type,
        options: [
          {
            label: selectAllFileLabels[type],
            value: uppercaseFirstLetter(type),
          },
          ...options,
        ],
      };
    });
  }

  // if we have not defined any restrictions, or all sections are present, or only 1 is present and we are allowing all of the extensions under that type
  const supportedTypeCount = Object.keys(allFileTypeExtensions).length;
  if (!sections.length || sections.length === supportedTypeCount) {
    // allow all file filters
    const ret = Object.entries(allFileTypeExtensions).reduce(
      (acc, [type, options]) => {
        const ret: GroupedFileTypeOptions = {
          type: type as SupportedTypes,
          options: [
            {
              label: selectAllFileLabels[type],
              value: uppercaseFirstLetter(type),
            },
            ...Object.values(options),
          ],
        };
        acc.push(ret);
        return acc;
      },
      [allFileOption]
    );
    return ret;
  }

  // don't allow selecting all of a type if we are not allowing all types
  return sections.flatMap((type): FileExtensionOption[] => {
    const allowedFileTypes = Object.keys(allowedFilters[type]) as SupportedMediaLibraryExtensions[];
    // if we allow all file types within a grouping, display the select all option, otherwise don't
    const options = allowedFileTypes.map((ext) => allFileTypeExtensions[type][ext]);
    return options;
  });
};
